/**
 * Calculates the duration in minutes between two time strings.
 *
 * @param {string} timeString - The time string in the format "HH:MM-HH:MM".
 * @returns {number} The duration in minutes.
 */
export function getDuration(timeString) {
  const [start, end] = timeString.split("-");

  const [startHours, startMins] = start.split(":");
  const [endHours, endMins] = end.split(":");

  const startTime = new Date(0, 0, 0, startHours, startMins, 0);
  const endTime = new Date(0, 0, 0, endHours, endMins, 0);

  // Check if the end time is on the next day
  if (endTime < startTime) {
    endTime.setDate(endTime.getDate() + 1);
  }

  const diff = endTime - startTime;

  const mins = Math.floor(diff / 1000 / 60);

  return mins;
}

/**
 * Converts a datetime string from "YYYY-MM-DD HH:mm:SS" format to "DD.MM.YYYY".
 *
 * @param {string} dateTime - The datetime string in "YYYY-MM-DD HH:mm:SS" format.
 * @returns {string} The formatted date string.
 */
export function getDate(dateTime) {
  const [date] = dateTime.split(/[ T]/);

  const [year, month, day] = date.split("-");

  return `${day}.${month}.${year}`;
}

/**
 * Extracts time from a datetime string in "YYYY-MM-DD HH:mm:SS" format as "HH:mm".
 *
 * @param {string} dateTime - The datetime string in "YYYY-MM-DD HH:mm:SS" format.
 * @returns {string} The formatted time string.
 */
export function getTime(dateTime) {
  const [, time] = dateTime.split(/[ T]/);

  const [hour, minute] = time.split(":");

  return `${hour}:${minute}`;
}

/**
 * Formats an array of date strings into a human-readable date range.
 *
 * @param {string[]} dates - Array of date strings parseable by the Date constructor.
 * @param {Object} [options] - Formatting options.
 * @param {"locale"|"eu"} [options.format="locale"] - Output format.
 *   - "locale": "18 - 21 Sep 2025" (same month) or "Sep 18, 2025 - Oct 2, 2025" (different months).
 *   - "eu": "18 - 21.09.2025" (same month) or "28.09.2025 - 02.10.2025" (different months).
 * @returns {string} The formatted date range string, or empty string if no dates provided.
 *
 * @example
 * formatDateRange(["2025-09-18", "2025-09-21"]);
 * // => "18 - 21 Sep 2025"
 *
 * @example
 * formatDateRange(["2025-09-18", "2025-09-21"], { format: "eu" });
 * // => "18 - 21.09.2025"
 */
export function formatDateRange(dates, options = {}) {
  if (!dates || dates.length === 0) return "";

  const { format = "locale" } = options;

  const parsedDates = dates.map((date) => new Date(date));
  parsedDates.sort((a, b) => a - b);

  const firstDate = parsedDates[0];
  const lastDate = parsedDates[parsedDates.length - 1];

  const sameMonth =
    firstDate.getMonth() === lastDate.getMonth() &&
    firstDate.getFullYear() === lastDate.getFullYear();

  if (format === "eu") {
    if (sameMonth) {
      return `${firstDate.getDate()} - ${lastDate.getDate()}.${String(firstDate.getMonth() + 1).padStart(2, "0")}.${firstDate.getFullYear()}`;
    }
    return `${firstDate.getDate().toString().padStart(2, "0")}.${(firstDate.getMonth() + 1).toString().padStart(2, "0")}.${firstDate.getFullYear()} - ${lastDate.getDate().toString().padStart(2, "0")}.${(lastDate.getMonth() + 1).toString().padStart(2, "0")}.${lastDate.getFullYear()}`;
  }

  // Default "locale" format
  if (sameMonth) {
    return `${firstDate.getDate()} - ${lastDate.getDate()} ${firstDate.toLocaleDateString("en-US", { month: "short", year: "numeric" })}`;
  }

  const localeOptions = { day: "numeric", month: "short", year: "numeric" };
  return `${firstDate.toLocaleDateString("en-US", localeOptions)} - ${lastDate.toLocaleDateString("en-US", localeOptions)}`;
}

/**
 * Returns the translated day name for a given date string.
 *
 * @param {string} dateString - A date string parseable by the Date constructor.
 * @param {Function} t - Translation function that receives a day name key (e.g., "monday").
 * @returns {string} The translated day name.
 */
export function getTranslatedDay(dateString, t) {
  const DAY_NAMES = {
    SUNDAY: t("sunday"),
    MONDAY: t("monday"),
    TUESDAY: t("tuesday"),
    WEDNESDAY: t("wednesday"),
    THURSDAY: t("thursday"),
    FRIDAY: t("friday"),
    SATURDAY: t("saturday"),
  };
  const key = new Date(dateString)
    .toLocaleDateString("en-US", { weekday: "long" })
    .toUpperCase();
  return DAY_NAMES[key];
}

/**
 * Returns the translated month name for a given date string.
 *
 * @param {string} dateString - A date string parseable by the Date constructor.
 * @param {Function} t - Translation function that receives a month name key (e.g., "january").
 * @returns {string} The translated month name.
 */
export function getTranslatedMonth(dateString, t) {
  const MONTH_NAMES = {
    JANUARY: t("january"),
    FEBRUARY: t("february"),
    MARCH: t("march"),
    APRIL: t("april"),
    MAY: t("may"),
    JUNE: t("june"),
    JULY: t("july"),
    AUGUST: t("august"),
    SEPTEMBER: t("september"),
    OCTOBER: t("october"),
    NOVEMBER: t("november"),
    DECEMBER: t("december"),
  };
  const key = new Date(dateString)
    .toLocaleDateString("en-US", { month: "long" })
    .toUpperCase();
  return MONTH_NAMES[key];
}
